import { useEffect, useRef, useState } from 'react'

import { createAudio, type AudioEngine } from './audio'
import * as C from './constants'
import { advance, createGame, drainEvents, multiplier, startRun, steer } from './engine'
import { attachInput } from './input'
import { createRenderer } from './render'
import type { GameEvent, GameState, ObstacleKind, RunStats } from './types'

export interface RunResult {
  score: number
  stats: RunStats
  crashKind: ObstacleKind | null
}

export interface Hud {
  score: number
  /** km/h. */
  speed: number
  combo: number
  multiplier: number
  /** Seconds of Bobi autopilot left. */
  autopilot: number
  /** Seconds of Sokar double points left. */
  doublePoints: number
}

export interface Bubble {
  /** Changes on every new bubble so the CSS animation restarts. */
  id: number
  /** i18n key, e.g. `bubble.bobi`. */
  key: string
}

export interface UseGameOptions {
  muted: boolean
  onCrash: (result: RunResult) => void
}

export interface UseGameResult {
  canvasRef: React.RefObject<HTMLCanvasElement | null>
  stageRef: React.RefObject<HTMLDivElement | null>
  hud: Hud
  bubble: Bubble | null
  /** 3 → 1 before the truck rolls; null once it does. */
  countdown: number | null
}

const COUNTDOWN_FROM = 3
const BUBBLE_TTL = 2.4
/** HUD refresh rate — the canvas runs at full rAF, React does not need to. */
const HUD_INTERVAL = 1 / 14

const EMPTY_HUD: Hud = {
  score: 0,
  speed: 0,
  combo: 0,
  multiplier: 1,
  autopilot: 0,
  doublePoints: 0,
}

/** Picks the most interesting line Bobi could say about this frame's events. */
function bubbleFor(events: GameEvent[], state: GameState): string | null {
  for (const e of events) {
    if (e.kind === 'bobi') return 'bubble.bobi'
    if (e.kind === 'sokar') return 'bubble.sokar'
  }
  for (const e of events) {
    if (e.kind === 'powerupEnd' && e.entityKind === 'bobi') return 'bubble.bobiEnd'
    if (e.kind === 'comboUp' && (e.combo ?? 0) >= 3) return 'bubble.combo'
    if (e.kind === 'comboLost') return 'bubble.comboLost'
    if (e.kind === 'nearMiss') return 'bubble.nearMiss'
  }
  if (state.speed > C.SPEED_MAX * 0.8) return 'bubble.fast'
  return null
}

export function useGame({ muted, onCrash }: UseGameOptions): UseGameResult {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const stageRef = useRef<HTMLDivElement | null>(null)
  const audioRef = useRef<AudioEngine | null>(null)
  const crashRef = useRef(onCrash)
  const [hud, setHud] = useState<Hud>(EMPTY_HUD)
  const [bubble, setBubble] = useState<Bubble | null>(null)
  const [countdown, setCountdown] = useState<number | null>(COUNTDOWN_FROM)

  crashRef.current = onCrash

  useEffect(() => {
    const canvas = canvasRef.current
    const stage = stageRef.current
    if (!canvas || !stage) return

    const state = createGame()
    const renderer = createRenderer(canvas)
    const audio = createAudio()
    audioRef.current = audio
    audio.setMuted(muted)
    audio.unlock()

    let raf = 0
    let last = performance.now()
    let hudAccumulator = 0
    let bubbleLeft = 0
    let bubbleId = 0
    let toStart = COUNTDOWN_FROM
    let shownCountdown = COUNTDOWN_FROM
    let fastAnnounced = false
    let finished = false

    const detach = attachInput(stage, {
      onLane: (dir) => {
        audio.unlock()
        if (steer(state, dir)) audio.handle({ kind: 'lane' })
      },
      onPrimary: () => audio.unlock(),
    })

    const observer = new ResizeObserver(() => renderer.resize())
    observer.observe(stage)

    const say = (key: string) => {
      bubbleId += 1
      bubbleLeft = BUBBLE_TTL
      setBubble({ id: bubbleId, key })
    }

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame)
      const elapsed = Math.min(C.MAX_FRAME_TIME, (now - last) / 1000)
      last = now

      if (toStart > 0) {
        toStart -= elapsed
        const next = Math.max(0, Math.ceil(toStart))
        if (next !== shownCountdown) {
          shownCountdown = next
          setCountdown(next === 0 ? null : next)
        }
        if (toStart <= 0) {
          startRun(state)
          audio.startMusic()
          say('bubble.start')
        }
      } else {
        advance(state, elapsed)

        const events = drainEvents(state)
        for (const event of events) audio.handle(event)

        const line = bubbleFor(events, state)
        if (line === 'bubble.fast') {
          // Speed brag fires once per run and never interrupts another line.
          if (!fastAnnounced && bubbleLeft <= 0) {
            fastAnnounced = true
            say(line)
          }
        } else if (line) {
          say(line)
        }

        audio.setIntensity((state.speed - C.SPEED_START) / (C.SPEED_MAX - C.SPEED_START))

        if (state.status === 'crashed' && !finished) {
          finished = true
          audio.stopMusic()
          setBubble(null)
          crashRef.current({
            score: Math.round(state.score),
            stats: { ...state.stats },
            crashKind: state.crashKind,
          })
        }
      }

      if (bubbleLeft > 0) {
        bubbleLeft -= elapsed
        if (bubbleLeft <= 0) setBubble(null)
      }

      hudAccumulator += elapsed
      if (hudAccumulator >= HUD_INTERVAL) {
        hudAccumulator = 0
        setHud({
          score: Math.round(state.score),
          speed: Math.round(C.speedo(state.speed)),
          combo: state.combo,
          multiplier: multiplier(state),
          autopilot: state.autopilot,
          doublePoints: state.doublePoints,
        })
      }

      renderer.draw(state, elapsed)
    }

    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
      detach()
      audio.dispose()
      audioRef.current = null
    }
    // The loop owns its own state for its whole lifetime; `muted` is applied
    // through the effect below so a toggle never restarts the run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    audioRef.current?.setMuted(muted)
  }, [muted])

  return { canvasRef, stageRef, hud, bubble, countdown }
}

export function formatTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}
